import { ref } from "vue";

// Global notification state
const notifications = ref([]);
let notificationIdCounter = 0;

// Notification types
export const NotificationTypes = {
  SUCCESS: "success",
  ERROR: "error",
  WARNING: "warning",
  INFO: "info",
};

// Default durations (in milliseconds)
export const NotificationDurations = {
  SUCCESS: 5000,
  ERROR: 8000,
  WARNING: 6000,
  INFO: 5000,
};

// Composable for notification system
export function useNotifications() {
  // Add a new notification
  const addNotification = (
    type,
    title,
    message,
    details = null,
    duration = null
  ) => {
    const id = ++notificationIdCounter;
    const notificationDuration =
      duration || NotificationDurations[type.toUpperCase()] || 5000;

    const notification = {
      id,
      type,
      title,
      message,
      details,
      duration: notificationDuration,
    };

    notifications.value.push(notification);

    // Auto-remove after duration
    setTimeout(() => {
      removeNotification(id);
    }, notificationDuration);

    return id;
  };

  // Remove a notification by ID
  const removeNotification = (id) => {
    const index = notifications.value.findIndex((n) => n.id === id);
    if (index > -1) {
      notifications.value.splice(index, 1);
    }
  };

  // Clear all notifications
  const clearAllNotifications = () => {
    notifications.value = [];
  };

  // Convenience methods for different notification types
  const showSuccess = (title, message, details = null, duration = null) => {
    return addNotification(
      NotificationTypes.SUCCESS,
      title,
      message,
      details,
      duration
    );
  };

  const showError = (title, message, details = null, duration = null) => {
    return addNotification(
      NotificationTypes.ERROR,
      title,
      message,
      details,
      duration
    );
  };

  const showWarning = (title, message, details = null, duration = null) => {
    return addNotification(
      NotificationTypes.WARNING,
      title,
      message,
      details,
      duration
    );
  };

  const showInfo = (title, message, details = null, duration = null) => {
    return addNotification(
      NotificationTypes.INFO,
      title,
      message,
      details,
      duration
    );
  };

  // Format Laravel validation errors
  const formatValidationErrors = (error) => {
    const details = [];

    if (error.response && error.response.data) {
      const errorData = error.response.data;

      // Handle Laravel validation errors
      if (errorData.errors && typeof errorData.errors === "object") {
        Object.keys(errorData.errors).forEach((field) => {
          const fieldErrors = Array.isArray(errorData.errors[field])
            ? errorData.errors[field]
            : [errorData.errors[field]];
          fieldErrors.forEach((err) => {
            details.push(`${field}: ${err}`);
          });
        });
      }

      // Handle single error message
      if (errorData.message && details.length === 0) {
        details.push(errorData.message);
      }
    }

    // Handle network errors
    if (error.code === "NETWORK_ERROR") {
      details.push(
        "Network connection error. Please check your internet connection."
      );
    }

    // Handle timeout errors
    if (error.code === "ECONNABORTED") {
      details.push("Request timeout. Please try again.");
    }

    // Handle 401 Unauthorized errors
    if (error.response && error.response.status === 401) {
      details.push("Session expired. Please log in again.");
    }

    // Handle 403 Forbidden errors
    if (error.response && error.response.status === 403) {
      details.push("You do not have permission to perform this action.");
    }

    // Handle 404 Not Found errors
    if (error.response && error.response.status === 404) {
      details.push("The requested resource was not found.");
    }

    // Handle 500 Server Error
    if (error.response && error.response.status === 500) {
      details.push("Server error. Please try again later.");
    }

    // Default error message
    if (details.length === 0) {
      details.push("An unexpected error occurred. Please try again.");
    }

    return details;
  };

  // Handle API errors with automatic notification
  const handleApiError = (error, customTitle = "Error") => {
    const details = formatValidationErrors(error);
    showError(customTitle, details[0], details.slice(1));
  };

  // Get notification icon
  const getNotificationIcon = (type) => {
    const icons = {
      [NotificationTypes.SUCCESS]: "mdi-check-circle",
      [NotificationTypes.ERROR]: "mdi-alert-circle",
      [NotificationTypes.WARNING]: "mdi-alert",
      [NotificationTypes.INFO]: "mdi-information",
    };
    return icons[type] || "mdi-information";
  };

  // Get notification icon color
  const getNotificationIconColor = (type) => {
    const colors = {
      [NotificationTypes.SUCCESS]: "white",
      [NotificationTypes.ERROR]: "white",
      [NotificationTypes.WARNING]: "white",
      [NotificationTypes.INFO]: "white",
    };
    return colors[type] || "white";
  };

  return {
    // Reactive data
    notifications,

    // Methods
    addNotification,
    removeNotification,
    clearAllNotifications,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    formatValidationErrors,
    handleApiError,
    getNotificationIcon,
    getNotificationIconColor,

    // Constants
    NotificationTypes,
    NotificationDurations,
  };
}
